import { forwardRef, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const DateTrigger = forwardRef(({ value, onClick }, ref) => (
  <span
    ref={ref}
    onClick={onClick}
    className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] cursor-pointer select-none"
  >
    {value}
  </span>
));

const NavChevron = ({ direction, onClick }) => (
  <div
    onClick={onClick}
    className="w-6 h-6 rounded-[4px] flex items-center justify-center cursor-pointer"
  >
    <svg
      className={`w-[14px] h-[14px] text-[#868DA6] ${direction === "right" ? "rotate-180" : ""}`}
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const hasLoggedTime = (entry) => {
  const isNonZero = (v) => v && typeof v === "string" && v.trim() !== "" && v.trim() !== "00:00" && v.trim() !== "__:__";
  return isNonZero(entry.regularTime) || isNonZero(entry.travelTime) || isNonZero(entry.overTime);
};

const DayIndicator = ({ startDateString, viewMode, currentDateChanged, timesheetData }) => {
  const [endDate] = useState(new Date());
  const [currentDate, setCurrentDate] = useState(new Date());
  const minDate = startDateString ? new Date(startDateString) : null;
  const toStartOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const highlightDates = useMemo(() => {
    if (!timesheetData || !startDateString) return [];
    const startDate = new Date(startDateString);
    const dateSet = new Set();
    for (const tech of timesheetData) {
      if (!tech.timesheet) continue;
      for (const entry of tech.timesheet) {
        if (hasLoggedTime(entry)) {
          const d = new Date(startDate);
          d.setDate(d.getDate() + Number(entry.dayDiff || 0));
          dateSet.add(d.toDateString());
        }
      }
    }
    return Array.from(dateSet).map((ds) => new Date(ds));
  }, [timesheetData, startDateString]);

  const handlePreviousDate = () => {
    const previousDate = new Date(currentDate);
    previousDate.setDate(previousDate.getDate() - 1);

    if (minDate && toStartOfDay(previousDate) < toStartOfDay(minDate)) return;

    setCurrentDate(previousDate);
  };

  const handleNextDate = () => {
    const nextDate = new Date(currentDate);
    nextDate.setDate(nextDate.getDate() + 1);

    if (toStartOfDay(nextDate) > toStartOfDay(endDate)) return;

    setCurrentDate(nextDate);
  };

  useEffect(() => {
    currentDateChanged(currentDate);
  }, [currentDate]);

  return (
    <div>
      {viewMode === "total" ? (
        <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">
          {moment(startDateString).format("MMM D, YYYY")} – {moment(endDate).format("MMM D, YYYY")}
        </span>
      ) : viewMode === "day" ? (
        <div className="flex items-center gap-[6px]">
          <NavChevron direction="left" onClick={handlePreviousDate} />
          <DatePicker
            selected={currentDate}
            onChange={(date) => date && setCurrentDate(date)}
            dateFormat="MMM d, yyyy"
            minDate={minDate}
            maxDate={endDate}
            customInput={<DateTrigger />}
            highlightDates={highlightDates}
            popperPlacement="bottom-end"
          />
          <NavChevron direction="right" onClick={handleNextDate} />
        </div>
      ) : null}
    </div>
  );
};

export default DayIndicator;
